import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import axios from 'axios';

const CampaignPerformance = ({ clientId, dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!clientId) return;
      
      try {
        const token = localStorage.getItem('token');
        const startDate = dateRange.startDate.toISOString().split('T')[0];
        const endDate = dateRange.endDate.toISOString().split('T')[0];
        
        const response = await axios.get(`/api/clients/${clientId}/campaigns`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate,
            endDate
          }
        });
        
        if (response.data.status === 'success') {
          setCampaigns(response.data.data.campaigns);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setLoading(false);
      }
    };
    
    fetchCampaigns();
  }, [clientId, dateRange]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value);
  };
  
  return (
    <Paper 
      sx={{ 
        p: 3, 
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(99, 102, 241, 0.1)',
        borderRadius: 3,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 700,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Desempenho de Campanhas
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Typography>Nenhuma campanha encontrada no período selecionado</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  Campanha
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  Conversões
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  Investimento
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  CPA
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  ROAS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign, index) => (
                <TableRow 
                  key={campaign._id}
                  sx={{
                    '&:hover': {
                      background: 'rgba(99, 102, 241, 0.03)',
                    },
                    '&:nth-of-type(even)': {
                      background: 'rgba(99, 102, 241, 0.01)',
                    },
                  }}
                >
                  <TableCell 
                    component="th" 
                    scope="row"
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary',
                    }}
                  >
                    {campaign.name}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {campaign.metrics.conversions}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {formatCurrency(campaign.metrics.spend)}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {formatCurrency(campaign.metrics.costPerConversion)}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      fontWeight: 600,
                      color: campaign.metrics.roas >= 1 ? 'success.main' : 'error.main',
                    }}
                  >
                    {campaign.metrics.roas.toFixed(2)}x
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default CampaignPerformance;
